// エアコン検索ボックス（sections/aicon-search-box.liquid の器を動かす）。
//
// 選択値から Shopify Storefront Filtering のクエリを組み立てて一覧へ遷移する:
//   メーカー → filter.p.vendor
//   シリーズ → filter.p.m.aicon.series（metafield。メーカーを選ぶまで欄自体を出さない）
//   畳数     → filter.p.m.aicon.room_size（metafield。Search & Discovery でフィルタ有効化が前提）
//   価格帯   → filter.v.price.gte / filter.v.price.lte（select の value は "min-max"。max 無しは "min-"）
//   並び替え → sort_by（Shopify の予約パラメータ。price-ascending / price-descending）
//   フリーワード → q（2026-09-08）。語が入っているときだけ遷移先を /search に切り替える
//               （コレクションの URL は語を受け付けない）。filter.* と sort_by は
//               /search でも同じ書式で効くので、そのまま一緒に乗せる。
// S&D 側でフィルタ未設定の項目はパラメータが無視されるだけで、遷移自体は成立する（fail-soft）。
//
// ★ 並び替えは絞り込みとは別の操作（2026-09-07）★
//   絞り込み（該当しない商品が消える）と並び替え（件数は同じで順番だけ変わる）は
//   別の操作なので、liquid 側で「この条件で検索する」の下・「条件をリセット」の上に置いた。
//   位置の都合で DOM 上は form の中にあるが、扱いは絞り込みと分けている。
//     ① 選んだ瞬間に効く（送信ボタンを押さない）
//     ② 既定は必ず「価格の安い順」（下の DEFAULT_SORT）
//     ③ 「条件をリセット」では消えない（native reset のあとに JS で選択を戻す）
//   sort_by を form.elements ではなく data-aicon-sort から引いているのは、
//   置き場所が変わっても JS を直さなくて済むようにするため。

// ★ 並び替えを選んでいないときの既定（日高さん指示 2026-09-07）★
//   「価格関係のソートを入れていないときは必ず安い順」。
//   これを付けないと Shopify はコレクションの管理画面側の並び順
//   （既定は手動 / ベストセラー）を使うため、機能や畳数で絞ったときの
//   並びが値段と無関係になっていた。
//
// ★ ただしフリーワード検索には当てない（2026-09-08）★
//   上の指示が対象にしているのは**コレクション**（管理画面の既定並び順が
//   手動/ベストセラーで、値段と無関係になる）。/search は公式に
//   「Results are sorted by relevance by default」で、既定そのものが妥当。
//   ここで安い順を強制すると「S22」で探した本人の狙った型番が
//   価格順の下に埋もれる。∴ 語があるときは sort_by を付けない。
const DEFAULT_SORT = 'price-ascending';

/** 並び替えの「関連度順」。値は空文字＝sort_by を付けない、の意味 */
const RELEVANCE_VALUE = '';
/** 設定（sort_relevance_label）が無いときのラベル。他の2つは section setting なので揃える */
const RELEVANCE_LABEL_FALLBACK = '関連度順';

class AiconSearchBox extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    // 並び替えは form の外にある。this から引く
    this.sortSelect = this.querySelector('[data-aicon-sort] select[name="sort_by"]');
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      location.href = this.buildUrl();
    });
    // 並び替えは選んだ瞬間に効く（絞り込みと違って確定操作を挟む意味がない）
    this.sortSelect?.addEventListener('change', () => {
      location.href = this.buildSortUrl();
    });
    // 語で検索した結果に着地したときだけ「関連度順」を選べるようにする（下の ensureRelevanceOption）

    // シリーズ（メーカー必須）。選択肢を取れるまでは欄を出さない
    this.seriesField = this.querySelector('[data-aicon-series-field]');
    this.seriesSelect = this.querySelector('[data-aicon-series]');
    this.form.elements['maker']?.addEventListener('change', () => this.syncSeries());
    /*
     * 「条件をリセット」はネイティブの form reset。後片付けが2つ必要になる。
     *
     * ① シリーズ欄
     *   reset は **DOM の初期値**に戻すので、
     *     ・メーカーが空に戻ったのに、差し替え済みの選択肢を持ったまま出続ける
     *     ・選択も「差し替え後の最初の option」に戻ってしまう
     *   という食い違いが残る。作り直す。
     *
     * ② 並び替え
     *   置き場所の都合で form の中にあるため reset の対象に入ってしまうが、
     *   **並び順は絞り込み条件ではない**（消す理由がない）。
     *   reset 前の値を控えて戻す。
     *
     * どちらも reset が終わったあと（＝次のタスク）に走らせる。
     * reset イベントの中で触っても、そのあとブラウザが初期値に戻してしまう。
     */
    this.form.addEventListener('reset', () => {
      const keptSort = this.sortSelect?.value;
      setTimeout(() => {
        /*
         * ★ `keptSort` を truthy で判定しない（2026-09-08）★
         *   関連度順の値は**空文字**（RELEVANCE_VALUE）なので、truthy 判定だと
         *   復元がスキップされ、native reset が DOM 初期値（= 価格の安い順）に戻す。
         *   リセットは遷移しないので、**画面は関連度順のまま「価格の安い順」と表示**
         *   される。上のコメントに書いた「並び順は絞り込み条件ではないので消さない」に
         *   反するうえ、restoreFromQuery で直したはずの「表示と実際の食い違い」の再発。
         */
        if (this.sortSelect && keptSort !== undefined) this.sortSelect.value = keptSort;
        this.syncSeries();
      }, 0);
    });
    // ★ 検索した条件を選択欄に戻す（日高さん指摘 2026-08-23）★
    //   遷移先の URL に条件は乗っているのに、選択欄は毎回プレースホルダに
    //   戻っていた。何で絞ったのかが画面から分からず、条件を足すには
    //   最初から選び直しになる。restoreFromQuery が buildUrl の逆をやる。
    this.restoreFromQuery();
    this.focusFromQuery();
    // 選択肢の取得は待たない。取れたら syncSeries が欄を出す（取れなければ出ないだけ）
    this.loadSeries();
  }

  /**
   * メーカー → シリーズの対応表を backend から取る。
   *
   * ★ 取れなくても何も壊さない ★
   *   シリーズ欄は hidden で始まる。ここが失敗すれば欄が出ないだけで、
   *   他の絞り込みと「この条件で検索する」はそのまま動く（他と同じ fail-soft）。
   *
   * ★ 選択肢を設定に持たせない理由 ★
   *   7メーカー×数十シリーズあり、商品の入れ替えで増減する。
   *   返る文字列は metafield aicon.series と同一（backend が
   *   product-mapper.toSeriesLabel を共有している）。絞り込みは完全一致なので、
   *   ここで文字列を加工してはいけない。
   */
  async loadSeries() {
    if (!this.seriesSelect) return;
    const apiBase = (this.dataset.apiBase || '').replace(/\/+$/, '');
    if (!apiBase) return;

    try {
      const res = await fetch(`${apiBase}/api/compare/product-series`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      if (!json?.success) return;
      const makers = json.data?.makers;
      if (!makers || typeof makers !== 'object') return;
      this.seriesByMaker = makers;
    } catch {
      // 通信できない／CORS で弾かれた。シリーズ欄を出さないだけ
      return;
    }

    // 読み込み前に URL から復元しようとしていた値をここで当てる
    this.syncSeries(this.pendingSeries);
    this.pendingSeries = undefined;
  }

  /**
   * 選ばれているメーカーに合わせてシリーズ欄を作り直す。
   *
   * ★ メーカーを外したら欄も消す ★
   *   シリーズ名は社をまたいで重複する（富士通の Z と 三菱の Z は別物）ので、
   *   メーカー無しのシリーズ指定は意味を持たない。選択も空に戻す
   *   （残しておくと buildUrl が「メーカー無し＋シリーズあり」を投げて 0件 になる）。
   *
   * @param {string} [preselect] 復元したい値。今のメーカーに無ければ無視する
   */
  syncSeries(preselect) {
    if (!this.seriesSelect || !this.seriesField) return;
    const maker = this.form.elements['maker']?.value || '';
    const list = (this.seriesByMaker && this.seriesByMaker[maker]) || [];

    if (!maker || list.length === 0) {
      this.seriesSelect.value = '';
      this.seriesField.hidden = true;
      return;
    }

    // 先頭のプレースホルダ option は liquid が置いている。残して以降を差し替える
    const placeholder = this.seriesSelect.options[0];
    this.seriesSelect.replaceChildren(placeholder);
    for (const name of list) {
      if (typeof name !== 'string' || name === '') continue;
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      this.seriesSelect.append(option);
    }

    this.seriesSelect.value =
      preselect && list.includes(preselect) ? preselect : '';
    this.seriesField.hidden = false;
  }

  /**
   * 現在の URL から選択欄を復元する（buildUrl の逆）。
   *
   * ★ 存在する option だけを選ぶ ★
   *   URL は人が編集できるので、知らない値で `select.value` に代入しても
   *   何も選ばれない（見た目はプレースホルダ）状態になる。option を探して
   *   見つかったときだけ入れる。
   *
   * ★ 価格帯は option の value を作り直して照合する ★
   *   value は "min-max"（max 無しは "min-"）。URL 側は gte / lte に分かれるので
   *   同じ形に組み直してから比べる。
   */
  restoreFromQuery() {
    let q;
    try {
      q = new URLSearchParams(location.search);
    } catch {
      return;
    }
    const put = (name, value) => {
      if (!value) return;
      const el = this.form.elements[name];
      if (!el || !el.options) return;
      const hit = [...el.options].some((o) => o.value === value);
      if (hit) el.value = value;
    };

    put('maker', q.get('filter.p.vendor'));
    put('room_size', q.get('filter.p.m.aicon.room_size'));
    put('grade', q.get('filter.p.m.aicon.grade'));
    put('feature', q.get('filter.p.m.aicon.feature_names'));
    /*
     * 専用ページ（/collections/maker-daikin など）では、条件が URL ではなくページそのものにある。
     * 選択欄に戻さないと「何で絞ったページか」が画面から分からない（上の 8/23 の指摘と同じ）。
     */
    const condition = this.jsonData('seoCondition');
    if (condition) {
      if (condition.kind === 'maker') put('maker', condition.value);
      if (condition.kind === 'series') put('maker', condition.maker);
      if (condition.kind === 'room_size') put('room_size', condition.value);
      if (condition.kind === 'grade') put('grade', condition.value);
      if (condition.kind === 'feature') put('feature', condition.value);
    }
    // フリーワード。/search?q=… から戻ってきたとき、何で探したかを欄に残す。
    // put は select 専用（options を見る）なので別に入れる。
    const keyword = this.form.elements['q'];
    if (keyword && 'value' in keyword && !keyword.options) {
      keyword.value = (q.get('q') || '').slice(0, 60);
    }

    /*
     * シリーズは option がまだ無い（loadSeries が非同期）。
     * put は「存在する option だけ選ぶ」ので今は当たらない。値を覚えておき、
     * 選択肢が入った時点で syncSeries が当てる。
     */
    this.pendingSeries =
      q.get('filter.p.m.aicon.series') ||
      (condition?.kind === 'series' ? condition.value : undefined) ||
      undefined;

    // 並び替えは put（存在する option だけ選ぶ）に通さず自前で当てる。
    // URL に無いときは既定の安い順を選んだ状態で見せる。実際の並びも
    // 同じ（テーマは常に sort_by を付けて遷移し、直リンクは
    // コレクション側の既定並び順を「価格の安い順」にしてある）。
    /*
     * 並び替えの表示を実際の並びと合わせる（2026-09-08 訂正）
     *
     * ★ 以前は URL に sort_by が無ければ必ず「価格の安い順」を選んで見せていた ★
     *   コレクションではそれで正しい（テーマが必ず sort_by を付けて遷移し、
     *   直リンクもコレクション側の既定並び順が安い順）。
     *   だが /search の既定は **関連度順**（公式）で、そこに sort_by が無い状態は
     *   「安い順」ではない。選択欄だけ安い順を表示すると**表示と実際が食い違う**。
     *   ∴ 語があって sort_by が無いときは「関連度順」の選択肢を差し込んで選ぶ。
     */
    if (this.sortSelect) {
      const sort = q.get('sort_by');
      const hit = sort && [...this.sortSelect.options].some((o) => o.value === sort);
      /*
       * ★ 語があるなら sort_by の有無にかかわらず選択肢を出す（2026-09-08）★
       *   以前は「sort_by が無いとき」だけ差し込んでいたため、
       *   /search?q=S22&sort_by=price-ascending からは**関連度順に戻す手段が無かった**
       *   （buildSortUrl 側に sort_by を消す実装があるのに到達できない片道切符）。
       *   結果ページ（/search）に限る。コレクションで出すと「必ず安い順」と紛れる。
       */
      if (this.onSearchResults() && (q.get('q') || '').trim()) this.ensureRelevanceOption();
      if (hit) {
        this.sortSelect.value = sort;
      } else if ((q.get('q') || '').trim()) {
        this.sortSelect.value = RELEVANCE_VALUE;
      } else {
        this.sortSelect.value = DEFAULT_SORT;
      }
    }

    const gte = q.get('filter.v.price.gte') || '';
    const lte = q.get('filter.v.price.lte') || '';
    if (gte || lte) put('price', `${gte}-${lte}`);
  }

  /**
   * ?focus=<欄> で来たら、その選択欄まで運んで開く準備をする。
   *
   *   ?focus=maker     … 左上メニューの「メーカーから選ぶ」（8/19 日高さん指示）
   *   ?focus=room_size … 左上メニューの「畳数から選ぶ」（8/20 日高さん指示）
   *
   * ★ メニューのリンク先はコードから変えられない ★
   *   メニューの項目は Shopify 管理画面のナビゲーション（linklist）で編集するもの。
   *   受け口だけテーマ側に用意し、リンク先を
   *     メーカーから選ぶ → /collections/all?focus=maker
   *     畳数から選ぶ     → /collections/all?focus=room_size
   *   に設定してもらう前提にする。パラメータが無ければ何もしないので、
   *   設定前でも壊れない。
   *
   * ★ select を勝手に開くことはできない ★
   *   ネイティブの select はスクリプトからは開けない。`showPicker()` は
   *   Chrome 系にしか無いうえ、**直前のユーザー操作（transient user activation）**を
   *   要求する。ページ遷移でその資格は失われるので、遷移してきた直後の呼び出しは
   *   必ず弾かれる。iOS Safari には showPicker 自体が無い。
   *
   *   ＝「プルダウンが開いた状態で着地する」ことは、ネイティブの select を使う限り
   *   実現できない。できるのは **そこまで運んで・フォーカスを当てて・目立たせる**
   *   ところまでで、あとは1タップで開く。
   *   （どうしても開いた状態にするなら、select をやめて自前のドロップダウンに
   *     作り替える必要がある。別途ご相談。）
   */
  focusFromQuery() {
    let focus = '';
    try {
      focus = new URLSearchParams(location.search).get('focus') || '';
    } catch {
      return;
    }
    // 受け付ける値だけを通す。知らない値で form.elements を引かない
    if (focus !== 'maker' && focus !== 'room_size') return;
    const target = this.form.elements[focus];
    if (!target) return;

    // レイアウトが落ち着いてから運ぶ。即実行だと画像の読み込みで位置がずれる。
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.focus({ preventScroll: true });

      // どこを操作すればよいかを目で分かるようにする。
      // フォーカスリングだけだと、スマホではソフトキーボードも出ず変化が乏しい。
      target.classList.add('is-aicon-focus-target');
      const clear = () => target.classList.remove('is-aicon-focus-target');
      target.addEventListener('change', clear, { once: true });
      target.addEventListener('blur', clear, { once: true });

      // 開けるブラウザでは開く。開けない場合は例外か no-op で、上の強調だけが残る
      try {
        target.showPicker?.();
      } catch {
        // transient user activation が無い（＝遷移直後）。想定内
      }
    });
  }

  /**
   * いま Shopify 標準の検索結果ページ（/search）にいるか。
   *
   * ★ ロケール接頭辞だけを許す ★
   *   `/ja/search` は対象、`/pages/search`・`/collections/search`・
   *   `/blogs/news/search` は対象外。末尾一致（`(^|\/)search\/?$`）だと
   *   それらにも当たる。いまは該当テンプレートが無く到達しないが、
   *   このセクションは presets を持つので、テーマエディタで handle `search` の
   *   ページに置かれた瞬間に踏む。
   */
  onSearchResults() {
    return /^(?:\/[a-z]{2}(?:-[a-z]{2,4})?)?\/search\/?$/.test(location.pathname);
  }

  buildUrl() {
    const params = new URLSearchParams();
    /*
     * フリーワード（2026-09-08）
     *
     * ★ 語があるときだけ /search へ ★
     *   /collections/all は q を受け付けない。Shopify 標準の検索ページ
     *   （routes.search_url。ロケール接頭辞は liquid が付ける）に
     *   q と type=product を付けて渡す。options[prefix]=last は前方一致
     *   （「S22」で S226ATES… に当たる）。filter.* と sort_by は下で
     *   共通に付ける（/search でも同じ書式で効く）。
     *   語が無ければ従来どおりコレクションへ（動きは一切変えない）。
     */
    const keyword = (this.form.elements['q']?.value || '').trim();
    /*
     * ★ 条件が「専用ページ」に当たるときはそこへ送る（たいせいさん依頼・2026-10-07）★
     *   6畳だけ・ダイキンだけ・ダイキン×6畳 のような選び方は、Google に見せる専用ページ
     *   （自動コレクション。backend/shopify-seo-collections.mjs）がある。
     *   /collections/all?…&sort_by=… は本番の robots.txt で Disallow されていて、
     *   Google から見て存在しないページになるため。
     *   当たらない選び方（価格帯つき・条件2つ以上・語あり）は従来どおり。
     */
    if (!keyword && !this.form.elements['price']?.value) {
      const route = this.seoRoute();
      if (route) return route;
    }
    const base = keyword
      ? this.dataset.searchUrl || '/search'
      : this.dataset.collectionUrl || '/collections/all';
    if (keyword) {
      params.set('q', keyword);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
    }
    const maker = this.form.elements['maker']?.value;
    if (maker) params.set('filter.p.vendor', maker);
    const roomSize = this.form.elements['room_size']?.value;
    if (roomSize) params.set('filter.p.m.aicon.room_size', roomSize);
    // 機能 → filter.p.m.aicon.feature_names（metafield・2026-08-21）
    //
    // ★ features ではなく feature_names を見る ★
    //   aicon.features は json 型（[{name, description}]）で、
    //   Search & Discovery は **json 型にフィルタを作れない**。
    //   そこで名称だけを list.single_line_text_field で別に持たせた
    //   （backend/src/services/shopify/product-mapper.ts の feature_names）。
    //   表示は features（説明つき）、絞り込みは feature_names と役割を分けている。
    //
    // ★ 有効化が前提 ★
    //   Search & Discovery で「商品 › aicon › feature_names」のフィルタを
    //   有効にし、Shopify Store Sync を1回流すこと。未整備だと**絞り込まれず
    //   全件が出る**（他の絞り込みと同じ fail-soft）。
    // グレード → filter.p.m.aicon.grade（metafield・2026-10-05）。
    // 値は backend utils/productGrade.ts の GRADES（スタンダード / ミドル / ハイグレード / プレミアム）。
    const grade = this.form.elements['grade']?.value;
    if (grade) params.set('filter.p.m.aicon.grade', grade);
    const feature = this.form.elements['feature']?.value;
    if (feature) params.set('filter.p.m.aicon.feature_names', feature);
    /*
     * シリーズ → filter.p.m.aicon.series（metafield・2026-09-07）
     *
     * ★ メーカーが無いときは付けない ★
     *   シリーズ名は社をまたいで重複するため、メーカー無しの指定は
     *   別社の同名シリーズを引き当てて 0件 になる。欄自体もメーカーを
     *   選ぶまで隠しているが、URL を手で編集された場合の保険としてここでも見る。
     */
    const series = maker ? this.seriesSelect?.value : '';
    if (series) params.set('filter.p.m.aicon.series', series);
    const price = this.form.elements['price']?.value;
    if (price) {
      const [min, max] = price.split('-');
      if (min) params.set('filter.v.price.gte', min);
      if (max) params.set('filter.v.price.lte', max);
    }
    /*
     * 並び替え。Shopify の予約パラメータなので filter.* とは別枠で付ける
     * （price-ascending / price-descending は Shopify 側の値。勝手な文字列にしない）。
     *
     * ★ 語が無いときは未選択でも必ず付ける ★
     *   付けないとコレクション側の既定並び順（管理画面の設定）に落ちる。
     * ★ 語があるときは選ばれていなければ付けない ★
     *   /search の既定は関連度順で、それが語検索では妥当（上の DEFAULT_SORT 参照）。
     */
    const sort = this.sortValue();
    if (sort) params.set('sort_by', sort);
    else if (!keyword) params.set('sort_by', DEFAULT_SORT);
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  }

  /** data-* に入れた JSON を読む。壊れていれば null（従来の動きに落とす） */
  jsonData(name) {
    try {
      const value = JSON.parse(this.dataset[name] || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch {
      return null;
    }
  }

  /**
   * いまの選択が専用ページに当たるなら、その URL を返す。当たらなければ null。
   *
   * ★ 形は価格.com と同じ ★
   *   ・条件1つ                       → その条件の専用ページ
   *   ・メーカー（シリーズ）＋条件1つ → メーカー（シリーズ）のページ＋絞り込み1つ
   *   それ以外は null（/collections/all で従来どおり全部の条件を効かせる）。
   *   SEO 上の寄せ先は snippets/meta-tags.liquid の canonical が別に持つ。
   *   ここで条件を落とすと、選んだ条件が効かないページへ連れて行くことになる。
   *
   * ★ sort_by は「高い順」のときだけ付ける ★
   *   専用ページは管理画面の並び順が安い順。安い順で sort_by を付けると、
   *   robots.txt で Disallow された URL への内部リンクが増えるだけになる。
   */
  seoRoute() {
    const map = this.jsonData('seoCollections');
    if (!map) return null;
    const value = (name) => this.form.elements[name]?.value || '';
    const maker = value('maker');
    const series = maker ? this.seriesSelect?.value || '' : '';
    const others = [
      ['room_size', 'filter.p.m.aicon.room_size', value('room_size')],
      ['feature', 'filter.p.m.aicon.feature_names', value('feature')],
      ['grade', 'filter.p.m.aicon.grade', value('grade')],
    ].filter(([, , v]) => v);
    if (others.length > 1) return null;

    let handle = '';
    const params = new URLSearchParams();
    if (maker) {
      handle = series ? map[`series:${maker}:${series}`] : map[`maker:${maker}`];
      if (!handle) return null;
      if (others.length === 1) params.set(others[0][1], others[0][2]);
    } else if (others.length === 1) {
      handle = map[`${others[0][0]}:${others[0][2]}`];
    }
    if (!handle) return null;

    if (this.sortValue() === 'price-descending') params.set('sort_by', 'price-descending');
    const base = `${(this.dataset.collectionsUrl || '/collections').replace(/\/+$/, '')}/${handle}`;
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  }

  /**
   * 「関連度順」の選択肢を先頭に1つだけ足す。
   * 語で検索した結果に着地したときにしか呼ばない（コレクションでは出さない）。
   */
  ensureRelevanceOption() {
    if (!this.sortSelect) return;
    if ([...this.sortSelect.options].some((o) => o.value === RELEVANCE_VALUE)) return;
    const option = document.createElement('option');
    option.value = RELEVANCE_VALUE;
    option.textContent = this.dataset.sortRelevanceLabel || RELEVANCE_LABEL_FALLBACK;
    this.sortSelect.prepend(option);
  }

  /**
   * いま選ばれている並び順。価格の2値のどちらでもなければ空文字（＝関連度順）。
   *
   * ★ 空文字を「安い順」に丸めない（2026-09-08）★
   *   丸めると語で検索したときに関連度が捨てられる。安い順への既定当てはめは
   *   buildUrl / buildSortUrl 側で「語が無いとき」に限って行う。
   */
  sortValue() {
    const value = this.sortSelect?.value;
    return value === 'price-descending' || value === 'price-ascending' ? value : RELEVANCE_VALUE;
  }

  /**
   * 並び替えだけを差し替えた URL。
   *
   * ★ いま出ている絞り込みは触らない ★
   *   並び順は絞り込みと直交する操作なので、location.search をそのまま引き継いで
   *   sort_by だけ入れ替える。フォームの選択値からは組み立てない
   *   （「この条件で検索する」を押していない編集中の値まで確定させてしまうため）。
   *
   * ★ 一覧ページ以外は通常の検索として扱う ★
   *   TOP にも同じボックスがあり、そこには引き継ぐ絞り込みが無い。
   *   一覧へ遷移する必要があるので buildUrl に任せる。
   */
  buildSortUrl() {
    /*
     * ★ startsWith ではなく includes ★
     *   Shopify のロケール／マーケットはパスに接頭辞を付ける（/en/collections/all）。
     *   startsWith だと一覧ページなのに buildUrl へ落ち、
     *   data-collection-url（/collections/all）へ飛んで**接頭辞と絞り込みの両方を失う**。
     *   いまは en.default の1ロケールだけなので実害は出ていないが、
     *   locales/ に30以上あるので公開された時点で踏む。
     */
    // /search（フリーワードの結果）でも q と絞り込みを引き継いで並び替えだけ差し替える
    if (!location.pathname.includes('/collections/') && !this.onSearchResults()) {
      return this.buildUrl();
    }
    let params;
    try {
      params = new URLSearchParams(location.search);
    } catch {
      return this.buildUrl();
    }
    // 「関連度順」に戻したときは sort_by を消す（付けたままでは価格順が残る）
    const sort = this.sortValue();
    if (sort) params.set('sort_by', sort);
    else params.delete('sort_by');
    // ページ送りの途中で並べ替えたら1ページ目に戻す（3ページ目のまま並べ替えても意味がない）
    params.delete('page');
    return `${location.pathname}?${params.toString()}`;
  }
}

if (!customElements.get('aicon-search-box')) {
  customElements.define('aicon-search-box', AiconSearchBox);
}
